import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { api } from '../App';
import { Plus, Trash2, TrendingUp, User, Edit2, X, Check, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const FamilyMembers = ({ user, setUser }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    relation: '',
    email: '',
    phone: '',
  });
  const [editFormData, setEditFormData] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const res = await api.get('/family-members');
      setMembers(res.data);
    } catch (err) {
      toast.error('Failed to load family accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/family-members', formData);
      toast.success('Family account added');
      setOpen(false);
      setFormData({ name: '', relation: '', email: '', phone: '' });
      fetchMembers();
    } catch (err) {
      toast.error('Failed to add account');
    }
  };

  const handleEdit = (member) => {
    setEditingId(member.id);
    setEditFormData({
      name: member.name,
      relation: member.relation,
      email: member.email || '',
      phone: member.phone || '',
    });
  };

  const handleSaveEdit = async (id) => {
    try {
      await api.put(`/family-members/${id}`, editFormData);
      toast.success('Account updated');
      setEditingId(null);
      fetchMembers();
    } catch (err) {
      toast.error('Failed to update account');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this account? This will also delete all associated holdings.')) return;
    try {
      await api.delete(`/family-members/${id}`);
      toast.success('Account deleted');
      fetchMembers();
    } catch (err) {
      toast.error('Failed to delete account');
    }
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="text-center py-20">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div data-testid="family-accounts-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">Family Accounts</h1>
            <p className="text-gray-600">Manage your family portfolio accounts</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setEditMode(!editMode)}
              className={`btn-outline flex items-center gap-2 ${editMode ? 'bg-amber-50 border-amber-500 text-amber-700' : ''}`}
            >
              <Settings size={18} />
              {editMode ? 'Exit Edit Mode' : 'Edit Mode'}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-account-button" className="btn-primary flex items-center gap-2">
                  <Plus size={20} />
                  Add Account
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Family Account</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name *</label>
                    <input
                      type="text"
                      data-testid="member-name-input"
                      className="input-field w-full"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Relation *</label>
                    <input
                      type="text"
                      data-testid="member-relation-input"
                      className="input-field w-full"
                      placeholder="e.g., Father, Mother, Spouse"
                      value={formData.relation}
                      onChange={(e) => setFormData({ ...formData, relation: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <input
                      type="email"
                      data-testid="member-email-input"
                      className="input-field w-full"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone</label>
                    <input
                      type="tel"
                      data-testid="member-phone-input"
                      className="input-field w-full"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <Button type="submit" data-testid="submit-member-button" className="btn-primary w-full">
                    Add Account
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {editMode && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            ⚠️ Edit Mode is ON - You can now edit or delete accounts. Click Exit Edit Mode when done.
          </div>
        )}

        {members.length === 0 ? (
          <div className="card text-center py-12" data-testid="empty-state">
            <User className="mx-auto mb-4 text-gray-400" size={48} />
            <h3 className="text-xl font-semibold mb-2">No family accounts yet</h3>
            <p className="text-gray-600 mb-4">Add your first family account or upload portfolio files</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {members.map((member) => (
              <div key={member.id} className="card hover:shadow-lg" data-testid={`member-card-${member.id}`}>
                {editingId === member.id ? (
                  // Edit Form
                  <div className="space-y-3">
                    <input
                      type="text"
                      className="input-field w-full"
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      placeholder="Name"
                    />
                    <input
                      type="text"
                      className="input-field w-full"
                      value={editFormData.relation}
                      onChange={(e) => setEditFormData({ ...editFormData, relation: e.target.value })}
                      placeholder="Relation"
                    />
                    <input
                      type="email"
                      className="input-field w-full"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                      placeholder="Email"
                    />
                    <input
                      type="tel"
                      className="input-field w-full"
                      value={editFormData.phone}
                      onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                      placeholder="Phone"
                    />
                    <div className="flex gap-2">
                      <Button onClick={() => handleSaveEdit(member.id)} className="btn-primary flex-1 flex items-center justify-center gap-1">
                        <Check size={16} /> Save
                      </Button>
                      <Button onClick={handleCancelEdit} className="btn-outline flex-1 flex items-center justify-center gap-1">
                        <X size={16} /> Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Display View
                  <>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-[#D1FAE5] rounded-full">
                          <User className="text-primary" size={24} />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">{member.name}</h3>
                          <p className="text-sm text-gray-500">{member.relation}</p>
                        </div>
                      </div>
                      {editMode && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEdit(member)}
                            className="p-2 hover:bg-blue-50 rounded-lg text-blue-600"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            data-testid={`delete-member-${member.id}`}
                            onClick={() => handleDelete(member.id)}
                            className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      )}
                    </div>
                    {member.email && (
                      <p className="text-sm text-gray-600 mb-1">📧 {member.email}</p>
                    )}
                    {member.phone && (
                      <p className="text-sm text-gray-600 mb-3">📱 {member.phone}</p>
                    )}
                    <button
                      data-testid={`view-portfolio-${member.id}`}
                      onClick={() => navigate(`/portfolio/${member.id}`)}
                      className="btn-outline w-full flex items-center justify-center gap-2 mt-4"
                    >
                      <TrendingUp size={18} />
                      View Portfolio
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FamilyMembers;
